<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
            {{ t("attendance.myAttendance") }}
        </h1>

        <!-- Check-in Card -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2
                class="text-sm font-semibold text-gray-900 dark:text-white mb-3"
            >
                {{ t("attendance.checkIn") }}
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {{ t("attendance.checkInHint") }}
            </p>

            <form
                @submit.prevent="handleCheckIn"
                class="flex flex-col sm:flex-row gap-3 sm:items-start"
            >
                <input
                    v-model="checkInCode"
                    type="text"
                    :placeholder="t('attendance.codePlaceholder')"
                    maxlength="6"
                    autocomplete="off"
                    class="form-input flex-1 max-w-xs font-mono uppercase tracking-widest text-lg"
                    :class="{ 'border-danger-500': checkInError }"
                />
                <button
                    type="submit"
                    :disabled="checkingIn || checkInCode.trim().length === 0"
                    class="btn btn-primary disabled:opacity-50"
                >
                    {{
                        checkingIn
                            ? t("attendance.checkingIn")
                            : t("attendance.checkInButton")
                    }}
                </button>
            </form>

            <p
                v-if="checkInError"
                class="mt-3 text-sm text-danger-600 dark:text-danger-400"
            >
                {{ checkInError }}
            </p>

            <div
                v-if="checkInResult"
                class="mt-4 rounded-lg p-4"
                :class="checkInResultClass"
            >
                <p class="text-sm font-medium">
                    {{ checkInResult }}
                </p>
            </div>
        </div>

        <!-- History -->
        <div
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div
                class="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3"
            >
                <h2 class="text-sm font-semibold text-gray-900 dark:text-white">
                    {{ t("attendance.history") }}
                </h2>
                <DateRangePicker v-model="dateRange" class="w-full sm:w-64" />
            </div>

            <div v-if="loading" class="p-4">
                <SkeletonLoader variant="table" :rows="5" :cols="5" />
            </div>

            <div v-else-if="error" class="p-4">
                <div
                    class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
                >
                    <p class="text-sm text-danger-700 dark:text-danger-300">
                        {{ error }}
                    </p>
                </div>
            </div>

            <div v-else-if="logs.length === 0" class="p-4">
                <EmptyState
                    icon="CalendarIcon"
                    :title="t('attendance.noHistory')"
                    :description="t('attendance.noHistoryHint')"
                />
            </div>

            <div v-else class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("attendance.date") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("attendance.subject") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("attendance.checkInTime") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("attendance.status") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="log in logs"
                            :key="log.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    formatDate(log.attendance?.attendance_date)
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    log.attendance?.class_subject?.subject
                                        ?.name || "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDateTime(log.check_in_time) }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                    :class="statusClass(log.status)"
                                >
                                    {{ statusLabel(log.status) }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <BasePagination
                v-if="logs.length > 0"
                :pagination="meta"
                :per-page="perPage"
                :per-page-options="[10, 15, 25, 50, 100]"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import DateRangePicker, {
    type DateRangeValue,
} from "@/components/DateRangePicker.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import BasePagination from "@/components/BasePagination.vue";
import attendanceApi, { AttendanceLog } from "@/api/attendance";
import NotificationService from "@/utils/notifications";

const { t } = useI18n();
const route = useRoute();

const logs = ref<AttendanceLog[]>([]);
const meta = ref<any>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const perPage = ref(15);
const page = ref(1);
const dateRange = ref<DateRangeValue>({ date_from: null, date_to: null });

watch(dateRange, () => {
    page.value = 1;
    fetchHistory();
});

const checkInCode = ref(
    typeof route.query.code === "string" ? route.query.code : "",
);
const checkingIn = ref(false);
const checkInError = ref<string | null>(null);
const checkInResult = ref<string | null>(null);
const checkInAlready = ref(false);

const checkInResultClass = computed(() => {
    if (!checkInResult.value) return "";
    return checkInAlready.value
        ? "bg-warning-50 dark:bg-warning-900/20 text-warning-800 dark:text-warning-200"
        : "bg-success-50 dark:bg-success-900/20 text-success-800 dark:text-success-200";
});

function statusLabel(status: string) {
    const key = `attendance.statuses.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
}

function statusClass(status: string) {
    const classes: Record<string, string> = {
        present:
            "bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200",
        absent: "bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200",
        late: "bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200",
        excused:
            "bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
}

async function fetchHistory() {
    loading.value = true;
    error.value = null;

    try {
        const response = await attendanceApi.getMy({
            page: page.value,
            per_page: perPage.value,
            date_from: dateRange.value.date_from || undefined,
            date_to: dateRange.value.date_to || undefined,
        });

        logs.value = response.data.items;
        meta.value = response.data.meta;
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch attendance";
    } finally {
        loading.value = false;
    }
}

async function handleCheckIn() {
    const code = checkInCode.value.trim().toUpperCase();

    if (!code) return;

    checkingIn.value = true;
    checkInError.value = null;
    checkInResult.value = null;
    checkInAlready.value = false;

    try {
        await attendanceApi.checkIn(code);
        checkInResult.value = t("attendance.checkInSuccess");
        checkInCode.value = "";
        await fetchHistory();
    } catch (err: any) {
        const errors = err.response?.data?.errors;
        const firstError = errors?.code?.[0];
        const message = firstError || err.response?.data?.message;

        if (message && message.toLowerCase().includes("already")) {
            checkInAlready.value = true;
            checkInResult.value = t("attendance.alreadyCheckedIn");
            checkInCode.value = "";
            await fetchHistory();
        } else {
            checkInError.value = t("attendance.checkInFailed");
            NotificationService.error(
                err.response?.data?.message || "Check-in failed",
            );
        }
    } finally {
        checkingIn.value = false;
    }
}

function formatDate(dateString?: string) {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function formatDateTime(dateString?: string | null) {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

function goToPage(newPage: number) {
    page.value = newPage;
    fetchHistory();
}

function handlePerPageChange(value: number) {
    perPage.value = value;
    page.value = 1;
    fetchHistory();
}

onMounted(() => {
    fetchHistory();
});
</script>
